/* eslint no-restricted-syntax: [
  "error",
  {
    "selector": "Literal[value='search']",
    "message": "Use DATA_TABLE_PERSISTENCE_KEYS.url.search."
  },
  {
    "selector": "Literal[value='sort']",
    "message": "Use DATA_TABLE_PERSISTENCE_KEYS.url.sort."
  },
  {
    "selector": "Literal[value='order']",
    "message": "Use DATA_TABLE_PERSISTENCE_KEYS.url.order."
  },
  {
    "selector": "Literal[value='exp']",
    "message": "Use DATA_TABLE_PERSISTENCE_KEYS.url.expanded."
  }
] */
import type {
  ColumnConfig,
  ColumnDataType,
  FilterOperator,
  FilterValue,
  TableRowData,
  ColumnStatePersistenceOptions,
  SortState,
} from '../types';
import { DATA_TABLE_PERSISTENCE_KEYS } from '../types';
import {
  ColumnDataTypes,
  FilterOperators,
  isValidNumericFilterRange,
  isValuelessOperator,
} from '../types';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { getLeafColumns } from './columnGroupUtils';

export type ColumnStatePersistenceMap = Record<
  string,
  ColumnStatePersistenceOptions
>;

export type ColumnSortPersistenceMap = Record<
  string,
  {
    sortKey: string;
  }
>;

const isFilterValueActive = (filter: FilterValue): boolean => {
  return (
    isValuelessOperator(filter.operator) ||
    filter.value.some(value => value.trim() !== '')
  );
};

// NUMBER filters must be a valid positional [min, max] range. Drop a stale or
// shared URL in any other shape (legacy single-value/IN-list, bad bounds) so it
// can't 400 the whole table — the same rules the query schema enforces.
const isPersistedFilterValid = (
  dataType: ColumnDataType | undefined,
  values: string[]
): boolean => {
  return (
    dataType !== ColumnDataTypes.NUMBER || isValidNumericFilterRange(values)
  );
};

const encodeFilterKey = (tableId: string, filter: FilterValue): string => {
  return `${tableId}.${filter.field}[${filter.operator}]`;
};

const encodeFilterValues = (values: string[]): string => {
  return values.map(value => encodeURIComponent(value)).join(',');
};

const getCurrentSearchParams = (): URLSearchParams => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  return new URLSearchParams(window.location.search);
};

const getUrlKeyToFieldMap = (
  persistenceMap: ColumnStatePersistenceMap
): Record<string, string> => {
  return Object.entries(persistenceMap).reduce<Record<string, string>>(
    (accumulator, [field, config]) => {
      accumulator[config.urlKey] = field;
      return accumulator;
    },
    {}
  );
};

const getSortKeyToFieldMap = (
  sortMap: ColumnSortPersistenceMap
): Record<string, string> => {
  return Object.entries(sortMap).reduce<Record<string, string>>(
    (accumulator, [field, config]) => {
      accumulator[config.sortKey] = field;
      return accumulator;
    },
    {}
  );
};

const decodeFilterKey = (
  tableId: string,
  key: string
): { urlKey: string; operator: string } | null => {
  const prefix = `${tableId}.`;
  if (!key.startsWith(prefix)) {
    return null;
  }

  const keyWithoutPrefix = key.slice(prefix.length);
  const match = keyWithoutPrefix.match(/^(.+)\[(.+)\]$/);

  if (!match) {
    return null;
  }

  return { urlKey: match[1], operator: match[2] };
};

const decodeFilterValues = (
  value: string,
  dataType?: ColumnDataType,
  operator?: FilterOperator
): string[] => {
  const decoded = value.split(',').map(item => {
    const trimmed = item.trim();
    if (!trimmed) {
      return '';
    }

    try {
      return decodeURIComponent(trimmed);
    } catch {
      return trimmed;
    }
  });

  // Preserve positional empties for numeric range filters so min-only/max-only
  // survive a refresh or shared URL; everything else strips empties as before.
  const isNumberRange =
    dataType === ColumnDataTypes.NUMBER &&
    (operator === FilterOperators.INCLUDES ||
      operator === FilterOperators.EXCLUDES);

  return isNumberRange ? decoded : decoded.filter(Boolean);
};

const getSortParamKeys = (
  tableId: string
): { sortKey: string; orderKey: string } => {
  return {
    sortKey: `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.sort}`,
    orderKey: `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.order}`,
  };
};

const getSearchParamKey = (tableId: string): string => {
  return `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.search}`;
};

const getExpandedParamKey = (tableId: string): string => {
  return `${tableId}.${DATA_TABLE_PERSISTENCE_KEYS.url.expanded}`;
};

const normalizeSearchValue = (value: string): string => {
  const trimmed = value.trim();
  return trimmed;
};

const encodeSearchValue = (value: string): string => {
  return encodeURIComponent(value);
};

const decodeSearchValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const clearTableParams = (
  searchParams: URLSearchParams,
  tableId: string
): void => {
  const prefix = `${tableId}.`;
  Array.from(searchParams.keys()).forEach(key => {
    if (key.startsWith(prefix)) {
      searchParams.delete(key);
    }
  });
};

const getStatePersistenceOption = <TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): ColumnStatePersistenceOptions | undefined => {
  if (column.filterable && column.statePersistence) {
    return { ...column.statePersistence, dataType: column.dataType };
  }

  return undefined;
};

const getSortPersistenceOption = <TRow extends TableRowData>(
  column: ColumnConfig<TRow>
): { field: string; sortKey: string } | undefined => {
  if (!column.sortable) {
    return undefined;
  }

  const field = column.sortField ?? column.id;
  const sortKey = column.sortKey ?? column.id;

  return { field, sortKey };
};

export const extractStatePersistenceConfig = <TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): ColumnStatePersistenceMap => {
  return getLeafColumns(columns).reduce<ColumnStatePersistenceMap>(
    (accumulator, column) => {
      const statePersistence = getStatePersistenceOption(column);

      if (statePersistence) {
        accumulator[column.id] = statePersistence;
      }
      // KIR-5098: the column's alternate field persists under its own key.
      if (column.filterable && column.alternateFilter?.urlKey) {
        accumulator[column.alternateFilter.field] = {
          urlKey: column.alternateFilter.urlKey,
          dataType: column.alternateFilter.dataType,
        };
      }

      return accumulator;
    },
    {}
  );
};

export const extractSortPersistenceConfig = <TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): ColumnSortPersistenceMap => {
  return getLeafColumns(columns).reduce<ColumnSortPersistenceMap>(
    (accumulator, column) => {
      const sortConfig = getSortPersistenceOption(column);
      if (sortConfig) {
        accumulator[sortConfig.field] = { sortKey: sortConfig.sortKey };
      }

      return accumulator;
    },
    {}
  );
};

/**
 * Extracts the set of valid sort field names from columns.
 * Used to validate persisted sort state from localStorage/URL.
 */
export const extractValidSortFields = <TRow extends TableRowData>(
  columns: ColumnConfig<TRow>[]
): Set<string> => {
  const sortConfig = extractSortPersistenceConfig(columns);
  return new Set(Object.keys(sortConfig));
};

export const getPersistedFilters = (
  filters: FilterValue[],
  persistenceMap: ColumnStatePersistenceMap
): FilterValue[] => {
  return filters
    .filter(filter => Boolean(persistenceMap[filter.field]))
    .filter(isFilterValueActive)
    .map(filter => ({
      ...filter,
      field: persistenceMap[filter.field].urlKey,
    }));
};

export const getPersistedSort = (
  sort: SortState | null | undefined,
  sortMap: ColumnSortPersistenceMap
): SortState | null => {
  if (!sort) {
    return null;
  }

  const mapped = sortMap[sort.field];
  if (!mapped) {
    return null;
  }

  return sort;
};

export const getPersistedSearch = (
  search: string | null | undefined
): string => {
  if (!search) {
    return '';
  }

  return normalizeSearchValue(search);
};

export const encodePersistedFiltersToParams = (
  tableId: string,
  filters: FilterValue[]
): Record<string, string> => {
  return filters.reduce<Record<string, string>>((params, filter) => {
    params[encodeFilterKey(tableId, filter)] = encodeFilterValues(filter.value);
    return params;
  }, {});
};

export const encodePersistedSortToParams = (
  tableId: string,
  sort: SortState | null | undefined,
  sortMap: ColumnSortPersistenceMap
): Record<string, string> => {
  if (!sort) {
    return {};
  }

  const mapped = sortMap[sort.field];
  if (!mapped) {
    return {};
  }

  const { sortKey, orderKey } = getSortParamKeys(tableId);

  return {
    [sortKey]: mapped.sortKey,
    [orderKey]: sort.order,
  };
};

export const encodePersistedSearchToParams = (
  tableId: string,
  search: string
): Record<string, string> => {
  if (!search) {
    return {};
  }

  const searchKey = getSearchParamKey(tableId);
  const encodedValue = encodeSearchValue(search);
  return {
    [searchKey]: encodedValue,
  };
};

export const decodeFiltersFromSearchParams = (
  tableId: string,
  persistenceMap: ColumnStatePersistenceMap,
  searchParams: URLSearchParams = getCurrentSearchParams()
): FilterValue[] => {
  const urlKeyMap = getUrlKeyToFieldMap(persistenceMap);
  const operatorSet = new Set(Object.values(FilterOperators));

  const filters: FilterValue[] = [];

  searchParams.forEach((value, key) => {
    const decodedKey = decodeFilterKey(tableId, key);
    if (!decodedKey) {
      return;
    }

    const { urlKey, operator } = decodedKey;
    const field = urlKeyMap[urlKey];

    if (!field || !operatorSet.has(operator as FilterValue['operator'])) {
      return;
    }

    const dataType = persistenceMap[field]?.dataType;
    const decodedValues = decodeFilterValues(
      value,
      dataType,
      operator as FilterOperator
    );
    if (
      decodedValues.length === 0 &&
      !isValuelessOperator(operator as FilterOperator)
    ) {
      return;
    }
    if (!isPersistedFilterValid(dataType, decodedValues)) {
      return;
    }

    filters.push({
      field,
      operator: operator as FilterValue['operator'],
      value: decodedValues,
    });
  });

  return filters.filter(isFilterValueActive);
};

export const decodeSortFromSearchParams = (
  tableId: string,
  sortMap: ColumnSortPersistenceMap,
  searchParams: URLSearchParams = getCurrentSearchParams()
): SortState | null => {
  const { sortKey, orderKey } = getSortParamKeys(tableId);
  const sortKeyValue = searchParams.get(sortKey);
  const orderValue = searchParams.get(orderKey);

  if (!sortKeyValue || !orderValue) {
    return null;
  }

  const sortKeyMap = getSortKeyToFieldMap(sortMap);
  const field = sortKeyMap[sortKeyValue];
  const orderValues = new Set<string>(Object.values(SortOrder));

  if (!field || !orderValues.has(orderValue)) {
    return null;
  }

  return {
    field,
    order: orderValue as SortOrder,
  };
};

export const decodeSearchFromSearchParams = (
  tableId: string,
  searchParams: URLSearchParams = getCurrentSearchParams()
): string | null => {
  const searchKey = getSearchParamKey(tableId);
  const searchValue = searchParams.get(searchKey);

  if (!searchValue) {
    return null;
  }

  const decoded = decodeSearchValue(searchValue);
  const normalized = normalizeSearchValue(decoded);

  if (!normalized) {
    return null;
  }

  return normalized;
};

export const applyEncodedParamsToSearchParams = (
  tableId: string,
  encodedParams: Record<string, string>,
  base?: URLSearchParams
): URLSearchParams => {
  const nextSearchParams = new URLSearchParams(
    base ?? getCurrentSearchParams()
  );
  clearTableParams(nextSearchParams, tableId);

  Object.entries(encodedParams).forEach(([key, value]) => {
    nextSearchParams.set(key, value);
  });

  return nextSearchParams;
};

export const encodePersistedExpandedToParams = (
  tableId: string,
  expandedRowIds: Set<string>
): Record<string, string> => {
  if (expandedRowIds.size === 0) {
    return {};
  }

  const expandedKey = getExpandedParamKey(tableId);
  // Encode all expanded row IDs as comma-separated values
  const encodedValues = Array.from(expandedRowIds)
    .map(id => encodeURIComponent(id))
    .join(',');

  return {
    [expandedKey]: encodedValues,
  };
};

export const decodeExpandedFromSearchParams = (
  tableId: string,
  searchParams: URLSearchParams = getCurrentSearchParams()
): Set<string> => {
  const expandedKey = getExpandedParamKey(tableId);
  const expandedValue = searchParams.get(expandedKey);

  if (!expandedValue) {
    return new Set();
  }

  const decodedIds = decodeFilterValues(expandedValue);
  return new Set(decodedIds);
};
